/*
 * 右侧聊天面板
 *
 * 三段结构：
 *   [消息历史区]      ← MessageContent 渲染 markdown + 10 种自定义代码块
 *   [引用 chips]     ← 从左侧文档选中并「加入到会话」的内容
 *   [输入框 + 发送]
 *
 * AI 消息内容由后端 SSE 推送 markdown 文本（含 ```photo-list 等代码块）。
 * 交互附件（quick-replies / export-panel 等）由 ChatAttachment 描述，独立渲染。
 */

import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  CloseOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined
} from '@ant-design/icons'
import { message } from 'antd'
import type { ChatMessage, ChatAttachment, MessagePart } from '../types'
import { getTextContent } from '../types'
import type { SelectedContent } from './DocumentPreview'
import { formatSelectedAsQuote } from './DocumentPreview'
import MessageContent from './MessageContent'
import { AttachmentRenderer, AttachmentActionHandler } from './attachments'
import ToolCallGroup, { ThinkingPlaceholder } from './blocks/ToolCallGroup'
import UserAskBlock from './blocks/UserAskBlock'
import UserAskChip from './blocks/UserAskChip'
import {
  FileSpreadsheetIcon,
  FileTextIcon,
  ImageIcon,
  PaperclipIcon,
  XIcon,
  LoaderIcon,
  ArrowUpIcon
} from './blocks/icons'
import { MessageActionContext } from './blocks/MessageActionContext'
import { SettingsPopover } from '../settings/SettingsPopover'
import { useAiClerkSettings } from '../settings/useSettings'
import {
  COMPOSER_ACCEPT_TYPES,
  classifyFile,
  getFileExt,
  resolveMediaType,
  validateComposerFile
} from '../utils/officeFileTypes'
import { xheyAi } from '../api'
import './ChatPanel.scss'

/** Composer 内部维护的附件状态机 */
export interface ComposerAttachment {
  id: string
  fileName: string
  ext: string
  /** 标准 MIME，发给后端 file part 用（"image/png" / "application/pdf" …） */
  mediaType: string
  size: number
  /** image / spreadsheet / word / pdf / text / other —— 决定 chip 图标和缩略图 */
  kind: ReturnType<typeof classifyFile>
  status: 'uploading' | 'done' | 'error'
  /** 上传完成后的 OSS URL（done 时一定有） */
  fileUrl?: string
  /** 本地 ObjectURL：图片在上传时就先用本地 blob 显示缩略图，
   *  上传完再切到 OSS 远端 URL；离开组件时 revoke */
  previewUrl?: string
  /** 上传失败的文案 / 校验不通过的原因 */
  error?: string
}

/** 发送给上层的附件结构（已上传完成的） */
export interface SentAttachment {
  url: string
  name: string
  mediaType: string
}

type ToolCallPart = Extract<MessagePart, { type: 'tool-call' }>

type UserAskPart = Extract<MessagePart, { type: 'user-ask' }>
type UserAskQuestionPart = UserAskPart['questions'][number]

type RenderBlock =
  | { kind: 'text'; text: string }
  | { kind: 'reasoning'; text: string; state?: 'streaming' | 'done' }
  | { kind: 'tool-group'; calls: ToolCallPart[] }
  | { kind: 'user-ask'; part: UserAskPart }
  | { kind: 'system-error'; message: string }

// user_ask 工具走独立 user-ask part 渲染交互卡片,后端同步 emit 的 tool-call
// 仅用作 LLM 状态机里的"等待用户确认"标记,不需要在前端工具组里再重复一行.
function isUserAskToolCall(part: ToolCallPart): boolean {
  return part.toolName === 'user_ask' || part.toolName === 'user-ask'
}

// 按 parts 的时间顺序交错分组:
//   - 物理相邻(中间无 text / user-ask)的 tool-call 合并成一个 tool-group →
//     ToolCallInline 自然产出 "代码搜索 ×3" 这类计数
//   - 遇到 text / user-ask 这类"可见块"就 flush 当前工具组, 另起新段
//     → 还原 "说一段 → 调几个工具 → 再说一段" 的真实节奏, 而不是把全部工具拍成一坨
//   - reasoning 不参与断组(它渲染为 null, 若 flush 会把一组工具劈成两个紧挨的空隙组),
//     仅用于 pickLatestThoughtLine 抓副文案
function groupParts(parts: MessagePart[]): RenderBlock[] {
  const out: RenderBlock[] = []
  let curTools: ToolCallPart[] = []
  const flushTools = () => {
    if (curTools.length > 0) {
      out.push({ kind: 'tool-group', calls: curTools })
      curTools = []
    }
  }

  for (const p of parts) {
    if (p.type === 'tool-call') {
      if (isUserAskToolCall(p)) continue // user_ask 由 user-ask block 承载, 不进工具组
      curTools.push(p)
    } else if (p.type === 'text') {
      flushTools()
      out.push({ kind: 'text', text: p.text })
    } else if (p.type === 'reasoning') {
      // 跳过空 / NO_REPLY 的 reasoning placeholder — agent 调 user_ask 后 LLM 习惯
      // emit 空 thinking + "NO_REPLY" 当作 silent reply, 后端屏蔽了 text 但
      // reasoning chip 仍显示, 用户误以为 agent 在处理输入.
      // 注意: reasoning 不 flush 工具组(渲染为 null, 断组只会留下视觉空隙).
      const trimmed = (p.text ?? '').trim()
      if (trimmed === '' || trimmed === 'NO_REPLY') continue
      out.push({ kind: 'reasoning', text: p.text, state: p.state })
    } else if (p.type === 'user-ask') {
      flushTools()
      out.push({ kind: 'user-ask', part: p })
    } else if (p.type === 'system-error') {
      // 系统错误卡放在自身位置, 先 flush 在前的工具组 (错误前的调用历史保留可见),
      // 错误卡渲染为独立红框警告 (见 L1360+ blocks 分派).
      flushTools()
      out.push({ kind: 'system-error', message: p.message })
    }
  }
  flushTools()
  return out
}

/** 从 message.parts 倒序找最后一个 reasoning 段, 返回清洗后的完整文本,
 *  用于 ThinkingPlaceholder / ToolCallGroup 的副文案 (多行 + 滚动).
 *
 *  收起规则: 最后一段 reasoning 的 state === 'done' 时返回 undefined —
 *  思考已结束就不该再显示副文案 (跟 SSE 结束后整个 placeholder 消失同语义).
 *
 *  文本清洗 (移植自 XHeyClaw-Web/components/thinking-block.tsx):
 *   - 去开头 "Reasoning:\n" (gateway pi-embedded formatter 加的)
 *   - 每行首尾 `_` 各剥一次 (italic 包裹, 转发中末 `_` 可能丢, 单独剥)
 *   - 截断 800 字防止超长 (一般 LLM 一段 reasoning < 500 字, 留 buffer)
 *
 *  "NO_REPLY" 占位也跳过 (跟 groupParts 同口径). */
const THOUGHT_MAX_CHARS = 800
function pickLatestThoughtLine(parts: MessagePart[]): string | undefined {
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i]
    if (p.type !== 'reasoning') continue
    // 思考结束就不显示 — 跟 placeholder 整体消失语义一致
    if ((p as any).state === 'done') return undefined
    const raw = ((p as any).text ?? '').trim()
    if (!raw || raw === 'NO_REPLY') continue
    const cleaned = raw
      .replace(/^\s*Reasoning:\s*\n?/, '')
      .split('\n')
      .map((l: string) => l.replace(/^_/, '').replace(/_$/, ''))
      .join('\n')
      .trim()
    if (!cleaned) continue
    return cleaned.length > THOUGHT_MAX_CHARS
      ? cleaned.slice(-THOUGHT_MAX_CHARS) // 取尾部 (最新),保持自动滚到底的语义
      : cleaned
  }
  return undefined
}

export interface QuoteItem extends SelectedContent {
  id: string
}

interface ChatPanelProps {
  messages: ChatMessage[]
  quotes: QuoteItem[]
  onRemoveQuote: (id: string) => void
  sending: boolean
  /** 第三参：composer 输入框手动添加的已上传完成的附件（结构化，不再是 URL 列表） */
  onSend: (
    message: string,
    quotes: QuoteItem[],
    attachments: SentAttachment[]
  ) => void
  /** 处理交互附件的 action（如 quick-reply 点击、导出格式选择） */
  onAttachmentAction?: AttachmentActionHandler
  onAbort?: () => void
  /** user_ask 结构化回复 */
  onUserAskSubmit?: (response: {
    pending_id: string
    answers: Record<string, any>
  }) => void
  agentName?: string
  className?: string
  placeholder?: string
  /** 外部预填输入框（quick action 用）。nonce 每次新预填时递增，
   * 内部以此判断是否要重设 input。*/
  prefillInput?: { text: string; nonce: number } | null
  /** 当前会话 id (透给 SettingsPopover 拉 context_usage; dev/test/staging 才用) */
  sessionId?: string
  /** 当前 conversation id (跟 sessionId 二选一即可) */
  conversationId?: string
  /**
   * 等候发送队列:SSE 进行中时用户继续点发送会入队,
   * 展示在输入框上方,每条可单独取消
   */
  pendingQueue?: Array<{ id: string; text: string }>
  onCancelPending?: (id: string) => void
  /**
   * 输入框下方的额外内容（在白色聊天背景容器内、input-wrap 之后）。
   * 调用方塞自定义节点，由 ChatPanel 包一层 .aiClerk-chat__composer-footer 排版容器。
   * 用途示例：积分入口 chip、底部状态条等。
   */
  composerFooter?: React.ReactNode
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  quotes,
  onRemoveQuote,
  sending,
  onSend,
  onAttachmentAction,
  onAbort,
  agentName = 'AI填表员',
  className = '',
  placeholder = '告诉 AI 填表员你想怎么改…',
  prefillInput,
  onUserAskSubmit,
  sessionId,
  conversationId,
  pendingQueue,
  onCancelPending,
  composerFooter
}) => {
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  // 智能跟随: container ref + 状态 ref. 用户在底部 → 自动跟; 用户往上滚 → 不打断.
  // 用 ref 不用 state — onScroll 频繁触发, state 会爆 rerender.
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const isFollowingRef = useRef<boolean>(true)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // 记录上一次消费过的 nonce，避免重渲时重复设置
  const lastPrefillNonceRef = useRef<number>(-1)

  // ─── 输入框附件（paperclip / 拖拽 / 粘贴；对齐 XHeyClaw-Web composer）
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([])
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const ingestFiles = useCallback((files: File[] | FileList) => {
    const list = Array.from(files)
    if (list.length === 0) return
    for (const f of list) {
      const id = `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      const ext = getFileExt(f.name)
      const kind = classifyFile(ext, f.type)
      const mediaType = resolveMediaType(f.name, f.type)
      const errMsg = validateComposerFile(f)
      if (errMsg) {
        setAttachments((prev) => [
          ...prev,
          {
            id,
            fileName: f.name,
            ext,
            mediaType,
            kind,
            size: f.size,
            status: 'error',
            error: errMsg
          }
        ])
        continue
      }
      // 图片：上传前就先 ObjectURL 显示本地缩略图，避免等远端回包
      const localPreview = kind === 'image' ? URL.createObjectURL(f) : undefined
      setAttachments((prev) => [
        ...prev,
        {
          id,
          fileName: f.name,
          ext,
          mediaType,
          kind,
          size: f.size,
          status: 'uploading',
          previewUrl: localPreview
        }
      ])
      xheyAi
        .uploadAttachment(f)
        .then((r) => {
          setAttachments((prev) =>
            prev.map((a) => {
              if (a.id !== id) return a
              // 切到远端 URL，释放本地 blob
              if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
              return {
                ...a,
                status: 'done',
                fileUrl: r.fileUrl,
                fileName: r.fileName || a.fileName,
                previewUrl: a.kind === 'image' ? r.fileUrl : undefined
              }
            })
          )
        })
        .catch((e) => {
          setAttachments((prev) =>
            prev.map((a) =>
              a.id === id
                ? {
                    ...a,
                    status: 'error',
                    error: String((e && e.message) || e || '上传失败')
                  }
                : a
            )
          )
        })
    }
  }, [])

  // 卸载时 revoke 残留的 ObjectURL，避免内存泄漏
  useEffect(() => {
    return () => {
      attachments.forEach((a) => {
        if (a.previewUrl && a.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(a.previewUrl)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePickFiles = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click()
  }, [])

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) ingestFiles(e.target.files)
      // 重置 value，方便用户连选同一个文件再触发
      e.target.value = ''
    },
    [ingestFiles]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      if (e.dataTransfer?.files?.length) ingestFiles(e.dataTransfer.files)
    },
    [ingestFiles]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault()
      setDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // 只有真的离开整个 wrap 才置 false（避免子元素冒泡时闪烁）
    if (e.currentTarget === e.target) setDragOver(false)
  }, [])

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const cd = e.clipboardData
      if (!cd) return
      // ① 优先 clipboardData.files —— 从文件系统复制的文件；Chrome 粘贴截图也常落这里
      const files: File[] =
        cd.files && cd.files.length > 0 ? Array.from(cd.files) : []
      // ② 兜底 clipboardData.items —— 从网页 / App 复制的图片、部分浏览器截图只在
      //    items 里（此时 files 为空）。抽出 image/* 的项，给没文件名的粘贴图补个名，
      //    否则附件卡片名会是空 / "image.png"。
      if (files.length === 0 && cd.items && cd.items.length > 0) {
        for (const it of Array.from(cd.items)) {
          if (it.kind !== 'file' || !it.type.startsWith('image/')) continue
          const raw = it.getAsFile()
          if (!raw) continue
          if (raw.name && /\.[a-z0-9]+$/i.test(raw.name)) {
            files.push(raw)
          } else {
            const ext = (it.type.split('/')[1] || 'png').split('+')[0]
            files.push(
              new File([raw], `粘贴图片-${Date.now()}.${ext}`, {
                type: it.type
              })
            )
          }
        }
      }
      if (files.length > 0) {
        // 有图片 / 文件 → 拦默认粘贴（否则文件名 / 图片二进制会以文本落进输入框）
        e.preventDefault()
        ingestFiles(files)
        return
      }
      // ③ 没拿到内容、但明显是"复制了文件"（从 Finder / 资源管理器复制文件时，浏览器
      //    paste 读不到文件字节、只给文件名）→ 别把文件名当文本贴进去，拦住并提示改用
      //    拖拽 / 回形针。判据：types 含 'Files'，或整段是个带图片/文档扩展名的裸文件名。
      const plainText = (cd.getData && cd.getData('text/plain')) || ''
      const looksLikeFileCopy =
        (cd.types && Array.from(cd.types).includes('Files')) ||
        /^[^\s/\\]+\.(png|jpe?g|gif|webp|bmp|heic|heif|svg|pdf|docx?|xlsx?|pptx?|csv)$/i.test(
          plainText.trim()
        )
      if (looksLikeFileCopy) {
        e.preventDefault()
        message.info(
          '复制的图片文件无法直接粘贴内容，请把文件拖进输入框，或点回形针选择'
        )
      }
      // 其余（真·文本）→ 不拦，正常粘贴
    },
    [ingestFiles]
  )

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id)
      if (target?.previewUrl && target.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(target.previewUrl)
      }
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  const isAnyUploading = attachments.some((a) => a.status === 'uploading')
  const hasErrorOnly =
    attachments.length > 0 && attachments.every((a) => a.status === 'error')

  // 输入框自动增高：跟随内容涨，最多翻倍（约 6 行），再多就内部滚动
  const autoResizeInput = useCallback(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    const MAX_PX = 200 // 与 SCSS max-height 对齐
    const next = Math.min(ta.scrollHeight, MAX_PX)
    ta.style.height = next + 'px'
  }, [])

  useEffect(() => {
    autoResizeInput()
  }, [input, autoResizeInput])

  // 智能跟随底部: 仅当用户处于底部 50px 内才自动滚.
  // 用户主动往上滚 → isFollowingRef=false → 新内容不打断阅读.
  // 用户回到底部 → isFollowingRef=true → 重新自动跟.
  // streaming 期间用 'auto' (瞬移) 而不是 'smooth' — smooth 滚还没完 messages 又变了, 会 jitter.
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    isFollowingRef.current = distFromBottom < 50
  }, [])

  // 用户发新消息(messages.length 增长) → 强制跟到底.
  // 即使用户刚才在翻历史,发新消息后明显的意图是"要看自己刚发的 + 后续 AI 回复".
  const prevMessagesLenRef = useRef(messages.length)
  useEffect(() => {
    if (messages.length > prevMessagesLenRef.current) {
      isFollowingRef.current = true
    }
    prevMessagesLenRef.current = messages.length
  }, [messages.length])

  // 跟随到底: 用 rAF 合并同一帧内的多次请求, 避免每个流式 token 都 read scrollHeight + write
  // scrollTop (强制同步 layout; 长会话大 DOM 下每次 layout 随消息数变贵 → 卡顿放大器).
  const followRafRef = useRef<number | null>(null)
  const scrollToBottomThrottled = useCallback(() => {
    if (!isFollowingRef.current) return
    if (followRafRef.current != null) return
    followRafRef.current = requestAnimationFrame(() => {
      followRafRef.current = null
      const el = messagesContainerRef.current
      if (el) el.scrollTop = el.scrollHeight
    })
  }, [])

  // 新消息到达 (count 变) → 跟随到底. 同一条消息流式 token 内的高度增长交给下面 ResizeObserver,
  // 不再挂 [messages] 每 token 跑.
  useEffect(() => {
    scrollToBottomThrottled()
  }, [messages.length, scrollToBottomThrottled])

  // SSE streaming 期间内容高度持续涨, ResizeObserver 监听 inner 高度变化跟随到底; 经 rAF 节流后
  // 每帧最多一次 layout, 不再是每 token 一次 (原来 [messages] effect + RO 双触发 = 每 token 2 次).
  useEffect(() => {
    const el = messagesContainerRef.current
    if (!el) return
    const inner = el.firstElementChild as HTMLElement | null
    if (!inner) return
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => scrollToBottomThrottled())
    ro.observe(inner)
    return () => {
      ro.disconnect()
      if (followRafRef.current != null)
        cancelAnimationFrame(followRafRef.current)
    }
  }, [scrollToBottomThrottled])

  useEffect(() => {
    if (quotes.length > 0 && textareaRef.current) textareaRef.current.focus()
  }, [quotes.length])

  // 外部 quick action 预填：nonce 变化 → 覆盖 input 并 focus
  useEffect(() => {
    if (!prefillInput) return
    if (prefillInput.nonce === lastPrefillNonceRef.current) return
    lastPrefillNonceRef.current = prefillInput.nonce
    setInput(prefillInput.text)
    const ta = textareaRef.current
    if (ta) {
      ta.focus()
      // 光标定位到最后一行末尾（方便继续补充）
      requestAnimationFrame(() => {
        ta.setSelectionRange(prefillInput.text.length, prefillInput.text.length)
      })
    }
  }, [prefillInput])

  const handleSubmit = useCallback(() => {
    // sending 中右下角只显示停止按钮,无发送入口;Enter 也一并禁用,
    // 用户必须先点停止结束当前回复才能再发(等候队列入口暂时关闭)
    if (sending) return
    const trimmed = input.trim()
    const sentAtts: SentAttachment[] = attachments
      .filter((a) => a.status === 'done' && a.fileUrl)
      .map((a) => ({
        url: a.fileUrl as string,
        name: a.fileName,
        mediaType: a.mediaType
      }))
    if (!trimmed && quotes.length === 0 && sentAtts.length === 0) return
    if (isAnyUploading) return
    onSend(trimmed, quotes, sentAtts)
    setInput('')
    // revoke 残留 blob URL，再清空 chip
    attachments.forEach((a) => {
      if (a.previewUrl && a.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(a.previewUrl)
      }
    })
    setAttachments([])
    // 发送后把高度恢复默认
    requestAnimationFrame(autoResizeInput)
  }, [
    input,
    quotes,
    attachments,
    sending,
    isAnyUploading,
    onSend,
    autoResizeInput
  ])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter 发送，Shift+Enter 换行（IME 输入法候选中不拦截）
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  // 给消息流里 markdown 自定义代码块（如 FileListBlock 的「保存为模板」按钮）
  // 提供一个"在当前会话里直接发一条用户消息"的能力，等价于用户输入框输入文本并发送，
  // 不附带 quotes / attachments。
  const messageActionValue = React.useMemo(
    () => ({
      sendMessage: (text: string) => {
        const trimmed = text.trim()
        if (!trimmed) return
        onSend(trimmed, [], [])
      }
    }),
    [onSend]
  )

  const userAskLookup = React.useMemo(() => {
    const lookup = new Map<string, UserAskPart>()
    for (const message of messages) {
      if (message.role !== 'assistant') continue
      for (const part of message.parts) {
        if (part.type === 'user-ask' && part.pendingId) {
          lookup.set(part.pendingId, part)
        }
      }
    }
    return lookup
  }, [messages])

  // 用户提交 user_ask 后发的 [user_ask_response] 消息里带 pending_id + answers,
  // 反向按 pending_id 索引出来 —— UserAskBlock 拿这个就能在已提交态直接展示用户
  // 当时选了什么(刷新后也能还原,因为消息持久化在 history).
  const userAskAnswersLookup = React.useMemo(() => {
    const lookup = new Map<string, Record<string, any>>()
    for (const message of messages) {
      if (message.role !== 'user') continue
      const raw = getTextContent(message).trim()
      if (!raw.startsWith('[user_ask_response]')) continue
      const rest = raw.replace(/^\[user_ask_response\]\s*/, '')
      const match = rest.match(/^(\{[\s\S]*\})/)
      if (!match) continue
      try {
        const payload = JSON.parse(match[1])
        const pid =
          typeof payload?.pending_id === 'string' ? payload.pending_id : ''
        const answers =
          payload?.answers && typeof payload.answers === 'object'
            ? payload.answers
            : null
        if (pid && answers) lookup.set(pid, answers)
      } catch {
        // ignore malformed payload
      }
    }
    return lookup
  }, [messages])

  return (
    <MessageActionContext.Provider value={messageActionValue}>
      <div className={`aiClerk-chat ${className}`}>
        {/* 顶部固定标题:与下方消息列表分层,滚动时常驻 */}
        <div className="aiClerk-chat__header">
          <div className="aiClerk-chat__header-avatar" aria-hidden>
            ✦
          </div>
          <span className="aiClerk-chat__header-title">{agentName}</span>
          <div className="aiClerk-chat__header-actions">
            <SettingsPopover
              sessionId={sessionId}
              conversationId={conversationId}
            />
          </div>
        </div>
        <div
          className="aiClerk-chat__messages"
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
        >
          <div className="aiClerk-chat__messages-inner">
            {messages.length === 0 && (
              <div className="aiClerk-chat__empty">
                {/* 与顶栏 header 头像同一个品牌符号 ✦，统一 agent 身份标识 */}
                <div className="aiClerk-chat__empty-avatar">✦</div>
                <div className="aiClerk-chat__empty-text">
                  你好，我是 {agentName}。
                  <br />
                  选中文档里的内容，告诉我你想做什么。
                </div>
              </div>
            )}
            {messages.map((msg, idx) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onAttachmentAction={onAttachmentAction}
                onUserAskSubmit={onUserAskSubmit}
                isLastMessage={idx === messages.length - 1}
                userAskLookup={userAskLookup}
                userAskAnswersLookup={userAskAnswersLookup}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {quotes.length > 0 && (
          <div className="aiClerk-chat__quotes">
            {quotes.map((q) => (
              <QuoteChip
                key={q.id}
                quote={q}
                onRemove={() => onRemoveQuote(q.id)}
              />
            ))}
          </div>
        )}

        <div
          className={
            'aiClerk-chat__input-wrap' + (dragOver ? ' is-drag-over' : '')
          }
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {/* 等候队列:SSE 进行中入队的消息,chip 形式展示;每条带编号 + 文字 + 取消图标。
              chip 默认 2 行 line-clamp,内容超长时右下角出"展开全部";展开后右下角变"收起"。 */}
          {pendingQueue && pendingQueue.length > 0 && (
            <div className="aiClerk-chat__pending-row">
              {pendingQueue.map((p, idx) => (
                <PendingChip
                  key={p.id}
                  idx={idx + 1}
                  text={p.text}
                  onCancel={() => onCancelPending && onCancelPending(p.id)}
                />
              ))}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="aiClerk-chat__att-row">
              {attachments.map((a) => (
                <ComposerAttachmentChip
                  key={a.id}
                  att={a}
                  onRemove={() => removeAttachment(a.id)}
                />
              ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            className="aiClerk-chat__input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            rows={2}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={COMPOSER_ACCEPT_TYPES}
            multiple
            hidden
            onChange={handleFileInputChange}
          />
          <div className="aiClerk-chat__attach-btn-wrap">
            <button
              type="button"
              className="aiClerk-chat__attach-btn"
              onClick={handlePickFiles}
              aria-label="添加附件"
              title="添加附件（支持图片、Word、Excel、PDF 等）"
            >
              <PaperclipIcon className="aiClerk-chat__attach-icon" />
            </button>
          </div>
          <div className="aiClerk-chat__actions">
            {/* SSE 进行中:仅显示停止按钮(去掉发送,避免误触);
                空闲:仅显示发送按钮 */}
            {sending && onAbort ? (
              <button
                type="button"
                className="aiClerk-chat__btn aiClerk-chat__btn--abort"
                onClick={onAbort}
                title="停止当前 AI 回复"
              >
                ⏹
              </button>
            ) : (
              <button
                type="button"
                className="aiClerk-chat__btn aiClerk-chat__btn--send"
                onClick={handleSubmit}
                disabled={
                  isAnyUploading ||
                  (!input.trim() &&
                    quotes.length === 0 &&
                    !attachments.some((a) => a.status === 'done'))
                }
                title={
                  isAnyUploading
                    ? '附件上传中…'
                    : hasErrorOnly
                      ? '请移除上传失败的附件或换一个文件'
                      : undefined
                }
              >
                <ArrowUpIcon className="aiClerk-chat__send-icon" />
              </button>
            )}
          </div>
        </div>
        {/* 输入框下方插槽：仍在白色聊天背景内，由调用方塞自定义节点(如积分入口 chip) */}
        {composerFooter && (
          <div className="aiClerk-chat__composer-footer">{composerFooter}</div>
        )}
      </div>
    </MessageActionContext.Provider>
  )
}

// ─── 工具：解析发送给后端的文本 → 展示层的 quotes / body ───
// 后端请求体里 quote 是连续 `> ...` 行（支持多行结构化 payload，算一个 quote）。
// 附件不在文本里，由 [消息附件] 标签 / file parts 单独处理。
function parseUserMessage(raw: string): {
  quotes: Array<{ summary: string; full: string }>
  body: string
} {
  const quotes: Array<{ summary: string; full: string }> = []
  const bodyLines: string[] = []

  const lines = raw.split('\n')
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    // 连续 > 行 = 一个 quote group
    if (/^>\s?/.test(line)) {
      const group: string[] = []
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        group.push(lines[i].replace(/^>\s?/, ''))
        i++
      }
      const full = group.join('\n').trim()
      if (full) {
        const firstLine = group[0].replace(/^\[单元格 [^\]]+\]\s*/, '').trim()
        const summary = firstLine || full.slice(0, 60)
        quotes.push({ summary, full })
      }
      continue
    }
    bodyLines.push(line)
    i++
  }

  const body = bodyLines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return { quotes, body }
}

type ParsedUserAskResponse = {
  pendingId: string
  title: string
  subtitle?: string
  rows: Array<{ label: string; value: string }>
}

function parseUserAskResponse(
  body: string,
  userAskLookup?: Map<string, UserAskPart>
): ParsedUserAskResponse | null {
  if (!body.startsWith('[user_ask_response]')) return null
  const rest = body.replace(/^\[user_ask_response\]\s*/, '')
  const match = rest.match(/^(\{[\s\S]*\})(?:\n+([\s\S]*))?$/)
  if (!match) return null

  let payload: any
  try {
    payload = JSON.parse(match[1])
  } catch {
    return null
  }

  const pendingId =
    typeof payload?.pending_id === 'string' ? payload.pending_id : ''
  const answers =
    payload?.answers && typeof payload.answers === 'object'
      ? payload.answers
      : {}
  const prompt = pendingId ? userAskLookup?.get(pendingId) : undefined
  const rows = prompt
    ? Object.keys(answers).map((key) => {
        const question = prompt.questions.find((q) => q.qid === key)
        return {
          // label 优先用 label_short (后端 LLM 给的短标签 e.g. "时间范围");
          // 缺失时回退到 question 全文 (旧 history / LLM 没给的兜底)
          label: question?.label_short || question?.question || key,
          value: formatUserAskAnswerValue(answers[key], question)
        }
      })
    : []

  const subtitle = typeof match[2] === 'string' ? match[2].trim() : ''
  return {
    pendingId,
    title: prompt?.title || '已确认填写计划',
    subtitle:
      subtitle && subtitle !== '确认以上填写计划，请继续执行。'
        ? subtitle
        : undefined,
    rows
  }
}

function formatUserAskAnswerValue(
  value: any,
  question?: UserAskQuestionPart
): string {
  if (value == null || value === '') return '留空'

  const formatOption = (raw: string) => {
    const opt = question?.options?.find((item) => item.value === raw)
    return opt?.label || raw
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '未选择'
    return value
      .map((item) =>
        typeof item === 'string' ? formatOption(item) : String(item)
      )
      .join('、')
  }

  if (typeof value === 'boolean') {
    return value ? '是' : '否'
  }

  if (
    value &&
    typeof value === 'object' &&
    typeof value.start === 'string' &&
    typeof value.end === 'string'
  ) {
    return `${value.start} 至 ${value.end}`
  }

  if (typeof value === 'string') {
    return formatOption(value)
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  return String(value)
}

// ─── 子组件：用户消息里的文件附件卡片 ─────────────
// 视觉对齐"引用 chip"风格：紫色淡底 + 头部分类标签 + 文件名主体。
//   ┌──────────────────────────────────┐
//   │ 📊 Excel 表格                     │ ← 头：类型 emoji + 类目（紫色小字）
//   │ 04-23-1_xxxx.xlsx                 │ ← 主：文件名，bold 截断
//   └──────────────────────────────────┘
const FILE_TYPE_LABEL: Record<string, { emoji: string; label: string }> = {
  pdf: { emoji: '📕', label: 'PDF 文档' },
  doc: { emoji: '📄', label: 'Word 文档' },
  docx: { emoji: '📄', label: 'Word 文档' },
  xls: { emoji: '📊', label: 'Excel 表格' },
  xlsx: { emoji: '📊', label: 'Excel 表格' },
  csv: { emoji: '📊', label: 'CSV 表格' },
  ppt: { emoji: '📑', label: 'PPT 演示' },
  pptx: { emoji: '📑', label: 'PPT 演示' },
  txt: { emoji: '📝', label: '文本文件' },
  md: { emoji: '📝', label: 'Markdown' }
}

const UserFileCard: React.FC<{ name: string; url: string }> = ({
  name,
  url
}) => {
  const ext = (name.split('.').pop() || '').toLowerCase()
  const meta = FILE_TYPE_LABEL[ext] || { emoji: '📎', label: '附件' }
  return (
    <a
      className="aiClerk-user-file-card"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={name}
    >
      <div className="aiClerk-user-file-card__bar" />
      <div className="aiClerk-user-file-card__body">
        <div className="aiClerk-user-file-card__label">
          <span className="aiClerk-user-file-card__emoji">{meta.emoji}</span>
          <span>{meta.label}</span>
        </div>
        <div className="aiClerk-user-file-card__name">{name}</div>
      </div>
    </a>
  )
}

// 极简两列回执: [短标签] [答案], 多 row 标签自动对齐.
// 删 eyebrow / title / subtitle (跟 AI ask 卡片重复, 视觉啰嗦).
const UserAskResponseCard: React.FC<{ response: ParsedUserAskResponse }> = ({
  response
}) => {
  if (response.rows.length === 0) return null
  return (
    <div className="aiClerk-user-ask-response">
      {response.rows.map((row, index) => (
        <div className="aiClerk-user-ask-response__row" key={index}>
          <span className="aiClerk-user-ask-response__label">{row.label}</span>
          <span className="aiClerk-user-ask-response__value">{row.value}</span>
        </div>
      ))}
    </div>
  )
}

// ─── 子组件：composer 附件 chip ──────────────────
// 视觉对齐 XHeyClaw-Web AttachmentUI：
//   - 图片：圆角方形缩略图（本地 blob → 上传后切换到 OSS URL）
//   - 文档：彩色图标（Word=蓝 / Excel=绿 / PDF=红 / 文本=琥珀 / 其他=灰）+ 文件名
//   - 上传中转圈，失败红框可 hover 看错误

const KIND_META: Record<
  ComposerAttachment['kind'],
  { color: string; Icon: React.FC<{ className?: string }> }
> = {
  spreadsheet: { color: '#22C55E', Icon: FileSpreadsheetIcon },
  word: { color: '#3B82F6', Icon: FileTextIcon },
  presentation: { color: '#D84315', Icon: FileTextIcon },
  pdf: { color: '#EF4444', Icon: FileTextIcon },
  text: { color: '#F59E0B', Icon: FileTextIcon },
  image: { color: '#7C3AED', Icon: ImageIcon },
  other: { color: '#A3A3A3', Icon: FileTextIcon }
}

/**
 * 等候发送队列单条 chip。
 *
 * 文本默认 -webkit-line-clamp: 2,内容稍长就被截到 …,用户读不全。这里加:
 *   1. mount 后用 ref 测量 scrollHeight vs clientHeight,判断是否真的截断
 *   2. 截断了 → 右下角出"展开全部";点了变"收起",同时把 line-clamp 解掉让全文展开
 *   3. 没截断(短消息一行就完) → 不出任何 toggle
 */
const PendingChip: React.FC<{
  idx: number
  text: string
  onCancel: () => void
}> = ({ idx, text, onCancel }) => {
  const textRef = useRef<HTMLSpanElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [isOverflow, setIsOverflow] = useState(false)

  // text 变化时(新消息进队 / 异步刷新 text)重新测量。clamp 状态下读 scrollHeight
  // 比 clientHeight 多至少 2px 才算真截断 —— 留点容差避免亚像素摆动。
  useEffect(() => {
    if (expanded) return // 展开态下 line-clamp 已撤,scrollHeight 必然 = clientHeight
    const el = textRef.current
    if (!el) return
    setIsOverflow(el.scrollHeight > el.clientHeight + 2)
  }, [text, expanded])

  return (
    <span className="aiClerk-chat__pending-chip">
      <span className="aiClerk-chat__pending-chip-idx">{idx}</span>
      <span className="aiClerk-chat__pending-chip-body">
        <span
          ref={textRef}
          className={
            'aiClerk-chat__pending-chip-text' + (expanded ? ' is-expanded' : '')
          }
          title={!expanded ? text : undefined}
        >
          {text || '空消息'}
        </span>
        {(isOverflow || expanded) && (
          <button
            type="button"
            className="aiClerk-chat__pending-chip-toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '收起' : '展开全部'}
          </button>
        )}
      </span>
      <button
        type="button"
        className="aiClerk-chat__pending-chip-close"
        onClick={onCancel}
        aria-label="取消"
      >
        <CloseOutlined />
      </button>
    </span>
  )
}

const ComposerAttachmentChip: React.FC<{
  att: ComposerAttachment
  onRemove: () => void
}> = ({ att, onRemove }) => {
  const meta = KIND_META[att.kind] || KIND_META.other
  const Icon = meta.Icon
  // 失败的图片附件没有缩略图可展示，退回普通 chip 布局（图标 + 文字），
  // 这样才有空间把失败原因显示出来，而不是一个 56×56 的红色空盒子
  const isImage = att.kind === 'image' && att.status !== 'error'
  const isError = att.status === 'error'

  return (
    <div
      className={
        'aiClerk-chat__att-chip' +
        (isImage ? ' is-image' : '') +
        (isError ? ' is-error' : '') +
        (att.status === 'uploading' ? ' is-uploading' : '')
      }
      title={att.error || att.fileName}
    >
      {isImage && att.previewUrl ? (
        <span className="aiClerk-chat__att-chip-thumb">
          <img
            src={att.previewUrl}
            alt={att.fileName}
            className="aiClerk-chat__att-chip-thumb-img"
          />
        </span>
      ) : (
        <span
          className="aiClerk-chat__att-chip-icon"
          // 失败态不染 kind 色，让图标继承 .is-error 的红；并换成 ✕ 告警图标
          // —— 失败的图片附件若仍用图片图标，"图片图标 + 红错误文字"语义很怪
          style={{ color: isError ? undefined : meta.color }}
        >
          {isError ? (
            <XIcon className="aiClerk-chat__att-chip-svg" />
          ) : (
            <span className="aiClerk-chat__att-chip-svg" />
          )}
        </span>
      )}
      {!isImage && (
        // 失败时直接把原因显示在 chip 上（红字 + 省略号截断），全文仍在 title；
        // 不再让用户必须 hover 才知道为什么失败
        <span className="aiClerk-chat__att-chip-name">
          {isError ? att.error || '上传失败' : att.fileName}
        </span>
      )}
      {att.status === 'uploading' && (
        <span className="aiClerk-chat__att-chip-spinner" aria-label="上传中">
          <LoaderIcon className="aiClerk-chat__att-chip-spinner-svg" />
        </span>
      )}
      <button
        type="button"
        className="aiClerk-chat__att-chip-remove"
        onClick={onRemove}
        aria-label="移除附件"
      >
        <XIcon className="aiClerk-chat__att-chip-remove-svg" />
      </button>
    </div>
  )
}

// ─── 子组件：引用 chip ──────────────────────────

const QuoteChip: React.FC<{ quote: QuoteItem; onRemove: () => void }> = ({
  quote,
  onRemove
}) => {
  const [expanded, setExpanded] = useState(false)
  const singleDocxCell =
    quote.docType === 'docx' &&
    quote.source === 'cell-click' &&
    quote.rangePayload?.rows === 1 &&
    quote.rangePayload?.cols === 1
      ? quote.rangePayload.cells[0]?.[0]
      : null
  // 范围 chip 头部带尺寸维度，让"是个区域"的语义一目了然
  // 例：区域 E2:G3 · 2行×3列 · 6格
  const rangeSize = (() => {
    const p = quote.rangePayload
    if (!p) return ''
    const total = p.rows * p.cols
    return ` · ${p.rows}行×${p.cols}列 · ${total}格`
  })()
  const label =
    quote.source === 'cell-range' && quote.cellRef
      ? `区域 ${quote.cellRef}${rangeSize}`
      : quote.source === 'cell-click' && quote.cellRef
        ? `单元格 ${quote.cellRef}`
        : '选中内容'

  // 生成全量 preview 行（CSS 控制默认显 2 行，展开后显全部可滚动）
  const previewLines: string[] = []
  if (singleDocxCell) {
    previewLines.push(singleDocxCell.isEmpty ? '—' : singleDocxCell.displayText)
  } else if (quote.rangePayload) {
    const p = quote.rangePayload
    // 即使 buildRangePayload 已跳过跨列合并非 master 列,这里 dedupe 做兜底:
    // 避免不同来源 RangePayload(如未来 docx 路径)出现 header 重复时再爆 chip
    const headerLine = Array.from(
      new Set(Object.values(p.headers).filter(Boolean))
    ).join(' │ ')
    if (headerLine) previewLines.push(headerLine)
    for (const row of p.cells) {
      // 跳过合并 non-master cell:它们的内容已在 master 位置展示,
      // 再显示一片 "│ — │ — │ ..." 只是噪音
      const visible = row.filter((c) => !c.isMergedPart)
      if (visible.length === 0) continue
      const line = visible
        .map((c) => {
          if (c.isEmpty) return '—'
          if (c.imageSrc) return '🖼️'
          return c.displayText || '—'
        })
        .join(' │ ')
      // 选区第一行恰好是 headerRow 时,cells row 0 内容跟 headerLine 同源,
      // 跳过避免 chip 里展示两遍同样的文本
      if (line === headerLine) continue
      previewLines.push(line)
    }
  } else {
    previewLines.push(quote.text)
  }

  // 判断是否需要显示「展开」按钮：内容多于 2 行才显示
  const hasOverflow =
    previewLines.length > 2 || previewLines.some((l) => l.length > 80)

  return (
    <div className={`aiClerk-quote${expanded ? ' is-expanded' : ''}`}>
      <div className="aiClerk-quote__bar" />
      <div className="aiClerk-quote__body">
        <div className="aiClerk-quote__label">📋 {label}</div>
        <div className="aiClerk-quote__preview">
          {previewLines.map((line, i) => (
            <div className="aiClerk-quote__text" key={i}>
              {line}
            </div>
          ))}
        </div>
      </div>
      {hasOverflow && (
        <button
          type="button"
          className="aiClerk-quote__expand"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? '收起' : '展开'}
          title={expanded ? '收起' : '展开'}
        >
          {expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        </button>
      )}
      <button
        type="button"
        className="aiClerk-quote__close"
        onClick={onRemove}
        aria-label="移除引用"
      >
        ✕
      </button>
    </div>
  )
}

// ─── 子组件：消息气泡 ────────────────────────────
//
// ⚡ 性能: 用 React.memo 包裹, 让父组件 (ChatPanel) state 变化时, props 没变的
// MessageBubble 跳过重渲. 类比 SwiftUI 给 View 加 Equatable 协议 — 框架 diff 时
// `==` 一致就不重算 body. 这里默认浅比较 (props 每个 key 用 === 比) 就够用,
// 因为上游 callback 已 useCallback + lookup 已 useMemo, 历史消息对象引用稳定.
// 没 memo 之前: 用户在输入框打一个字 → ChatPanel setInput → 50 条 MessageBubble
// 全部重新算 body (即使内容没变). 加 memo 后: 只有 message 真正变化的那条
// (通常是 streaming 中的最后一条) 重渲, 其余 49 条 props 引用相等直接跳过.
interface MessageBubbleProps {
  message: ChatMessage
  onAttachmentAction?: AttachmentActionHandler
  onUserAskSubmit?: (response: {
    pending_id: string
    answers: Record<string, any>
  }) => void
  isLastMessage?: boolean
  userAskLookup?: Map<string, UserAskPart>
  userAskAnswersLookup?: Map<string, Record<string, any>>
}

// ⚡ 自定义比较器 — 默认浅比较会被 userAskLookup/userAskAnswersLookup 每个流式 token 换的新
// Map 引用 (ChatPanel 里这俩 useMemo dep=[messages], 每 token messages 重建) 对**所有**
// MessageBubble 整体失效 → 每 token 重渲全部历史消息 (会话越长越卡的头号原因).
// 这里: message 引用未变即视作本条内容未变; 只对真含 user-ask part 的消息, 按 pendingId 比对它
// 依赖的 lookup 条目 (问题本体 + 用户答案); 其余消息 (绝大多数) 忽略 lookup 引用变化 → 流式时
// 只有正在变的那条重渲, 历史消息全部跳过. (onAttachmentAction/onUserAskSubmit 在调用处 useCallback
// 稳定, 故可参与 !== 比较.)
function messageBubblePropsAreEqual(
  prev: MessageBubbleProps,
  next: MessageBubbleProps
): boolean {
  if (
    prev.message !== next.message ||
    prev.isLastMessage !== next.isLastMessage ||
    prev.onAttachmentAction !== next.onAttachmentAction ||
    prev.onUserAskSubmit !== next.onUserAskSubmit
  ) {
    return false
  }
  for (const part of next.message.parts) {
    if (part.type !== 'user-ask') continue
    const pid = part.pendingId
    if (!pid) continue
    if (prev.userAskLookup?.get(pid) !== next.userAskLookup?.get(pid))
      return false
    if (
      prev.userAskAnswersLookup?.get(pid) !==
      next.userAskAnswersLookup?.get(pid)
    ) {
      return false
    }
  }
  return true
}

const MessageBubble = React.memo<MessageBubbleProps>(
  ({
    message,
    onAttachmentAction,
    onUserAskSubmit,
    isLastMessage,
    userAskLookup,
    userAskAnswersLookup
  }) => {
    // showReasoning toggle (调试设置 → "显示思考过程") 控制 ThoughtTail 副文案是否展示;
    // 关闭时即使 reasoning 流式中也只显示 "思考中…" 干瘪态.
    // hook 必须在条件 return 之前调用 (React rules-of-hooks).
    const { showReasoning } = useAiClerkSettings()
    const isUser = message.role === 'user'

    if (isUser) {
      // <aiclerk-task-init>：work 页自动构造的 task trigger (用户没主动输入), 整条不渲染.
      // 用户视角: 进 work 页 = AI 自动开始. LLM 仍正常收到 (history 持久化, 上下文完整).
      const rawTextRaw = getTextContent(message)
      // 会话级入口标记 <aiclerk-scene> / <aiclerk-task-name> 可能追加在 task-init 之后;
      // 先剥掉再判整条隐藏, 否则末尾锚定 $ 不匹配会让 trigger 气泡漏出.
      const rawTextForHideTest = rawTextRaw
        .replace(/\s*<aiclerk-scene>[\s\S]*?<\/aiclerk-scene>/g, '')
        .replace(/\s*<aiclerk-task-name>[\s\S]*?<\/aiclerk-task-name>/g, '')
      if (
        /^\s*<aiclerk-task-init>[\s\S]*<\/aiclerk-task-init>\s*$/.test(
          rawTextForHideTest
        )
      ) {
        return null
      }

      // 文件附件：优先用结构化 file parts，兼容历史 [name](url) 文本格式
      const fileParts = message.parts.filter(
        (p): p is Extract<MessagePart, { type: 'file' }> => p.type === 'file'
      )
      // 文本气泡：剥掉自动注入的几段标签，文本里只保留用户输入
      // <aiclerk-sample-context>：参考样例 diff 文本，仅给后端 / agent 看，UI 不显示
      // <business-context>: home 页用户填的"业务背景"一句话, 嵌在 task-init 内部
      // 已经被整条不渲染兜住; 这里再 strip 一次防御未来若有人改了 firstMessage
      // 结构 (task-init 外独立带 business-context) 时仍能剥干净.
      let rawText = rawTextRaw
      rawText = rawText
        .replace(/\n*<message-attachments>[\s\S]+?<\/message-attachments>/g, '')
        .replace(/\n*<current-doc>[\s\S]+?<\/current-doc>/g, '')
        .replace(/\n*<aiclerk-scene>[\s\S]*?<\/aiclerk-scene>/g, '')
        .replace(/\n*<aiclerk-task-name>[\s\S]*?<\/aiclerk-task-name>/g, '')
        .replace(/\n*<aiclerk-blank-guide>[\s\S]*?<\/aiclerk-blank-guide>/g, '')
        .replace(
          /\n*<aiclerk-sample-context>[\s\S]+?<\/aiclerk-sample-context>/g,
          ''
        )
        .replace(/\n*<business-context>[\s\S]+?<\/business-context>/g, '')
        .trimEnd()
      const { quotes, body } = parseUserMessage(rawText)
      const askResponse = parseUserAskResponse(body, userAskLookup)
      // [user_ask_response] 用户消息整条不渲染:用户的选择已经由对应 UserAskBlock
      // 在已提交态里展示成结果摘要(通过 userAskAnswersLookup 拿到 answers),不再
      // 在用户消息层重复一张回执卡片
      if (askResponse) return null

      // 附件来源唯一：file parts（由 send 路径写入或 hydrate 解析 [消息附件] 块得到）
      const seenUrls = new Set<string>()
      const images: Array<{ url: string; name: string }> = []
      const files: Array<{ url: string; name: string }> = []
      for (const p of fileParts) {
        if (seenUrls.has(p.url)) continue
        seenUrls.add(p.url)
        if (p.mediaType && p.mediaType.startsWith('image/')) {
          images.push({ url: p.url, name: p.name })
        } else {
          files.push({ url: p.url, name: p.name })
        }
      }

      return (
        <div className="aiClerk-msg aiClerk-msg--user" data-role="user">
          <div
            className={
              'aiClerk-msg__bubble' +
              (askResponse ? ' aiClerk-msg__bubble--ask' : '')
            }
          >
            {/* 图片缩略图（右对齐，1-2 张并排） */}
            {images.length > 0 && (
              <div className="aiClerk-user-images">
                {images.map((img, i) => (
                  <a
                    key={i}
                    className="aiClerk-user-image"
                    href={img.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={img.name}
                  >
                    <img src={img.url} alt={img.name} />
                  </a>
                ))}
              </div>
            )}
            {/* 文件附件卡片（右对齐） */}
            {files.length > 0 && (
              <div className="aiClerk-user-files">
                {files.map((f, i) => (
                  <UserFileCard key={i} name={f.name} url={f.url} />
                ))}
              </div>
            )}
            {quotes.length > 0 && (
              <div className="aiClerk-user-quotes">
                {quotes.map((q, i) => (
                  <details className="aiClerk-user-quote" key={i}>
                    <summary>
                      <div className="aiClerk-user-quote__bar" />
                      <div className="aiClerk-user-quote__text">
                        {q.summary}
                      </div>
                    </summary>
                    {q.full !== q.summary && (
                      <pre className="aiClerk-user-quote__full">{q.full}</pre>
                    )}
                  </details>
                ))}
              </div>
            )}
            {askResponse ? (
              <UserAskResponseCard response={askResponse} />
            ) : body ? (
              <div className="aiClerk-msg__text">{body}</div>
            ) : null}
          </div>
        </div>
      )
    }

    const blocks = groupParts(message.parts)
    const isStreaming = !!message.streaming

    // 末尾全局进度指示器:整轮 message 还在流式中时, 在所有块之后挂一个
    // spinner + "正在处理". 各工具组只反映自身工具的终态(✓/✗ 或自身 running 的
    // spinner), 不再被整轮 streaming 劫持成"思考中" —— 交错布局下那会让所有历史
    // 工具组同时显示"思考中", 还会出现 "✓完成 + spinner" 自相矛盾.
    // 抑制两种情况避免重复/语义冲突:
    //  - 末块是 tool-group 且其中有正在跑的工具 → 该组自己已转圈, 不再叠加
    //  - 末块是 user-ask 交互卡片 → 在等用户输入, 不该转圈
    const lastBlock = blocks[blocks.length - 1]
    const lastGroupRunning =
      !!lastBlock &&
      lastBlock.kind === 'tool-group' &&
      lastBlock.calls.some(
        (c) => c.state === 'input-streaming' || c.state === 'input-available'
      )
    const showProcessing =
      isStreaming && !lastGroupRunning && lastBlock?.kind !== 'user-ask'

    // reasoning 不再单独渲染"已思考"折叠按钮 —— 流式中由 ThinkingPlaceholder /
    // ToolCallGroup 的 ThoughtTail 副文案展示, 结束后随 state=done 自动收起.
    // reasoning block 仅用于 pickLatestThoughtLine 抓 text, 不计入 hasVisibleBlock.
    const hasVisibleBlock = blocks.some((b) => b.kind !== 'reasoning')

    return (
      <div className="aiClerk-msg aiClerk-msg--ai" data-role="assistant">
        <div className="aiClerk-msg__content">
          <div className="aiClerk-msg__main">
            {(hasVisibleBlock || showProcessing) && (
              <div className="aiClerk-msg__body">
                {blocks.map((b, i) => {
                  // reasoning block 不渲染 —— 流式中由末尾"正在处理"指示器承载过程状态,
                  // 结束后随 state=done 自动收起 (用户明确不要"已思考"折叠按钮)
                  if (b.kind === 'reasoning') return null
                  if (b.kind === 'tool-group') {
                    // 工具组只反映自身工具状态(running → spinner, 否则 ✓/✗),
                    // 不传整轮 streaming —— 真空期的"正在处理"由末尾全局指示器承载.
                    return <ToolCallGroup key={`tg-${i}`} calls={b.calls} />
                  }
                  if (b.kind === 'user-ask' && onUserAskSubmit) {
                    const submitted = b.part.pendingId
                      ? userAskAnswersLookup?.get(b.part.pendingId)
                      : undefined
                    // 新协议: displayAs='chip' 且未提交 → 渲染 chip(完整卡片走底部 ActionSheet).
                    // submitted 或 legacy(displayAs 缺省) → 走原 UserAskBlock 路径.
                    if (b.part.displayAs === 'chip' && !submitted) {
                      return <UserAskChip key={`uac-${i}`} part={b.part} />
                    }
                    return (
                      <UserAskBlock
                        key={`ua-${i}`}
                        part={b.part}
                        isLastMessage={!!isLastMessage}
                        onSubmit={onUserAskSubmit}
                        submittedAnswers={submitted}
                      />
                    )
                  }
                  if (b.kind === 'text') {
                    return <MessageContent key={`t-${i}`} content={b.text} />
                  }
                  if (b.kind === 'system-error') {
                    // 系统级错误卡: 跟 AI 文本 / 工具组分开, 视觉是淡红警告条 +
                    // ⚠️ icon + 中性文案. 不含任何原始 errorText / 内部信息
                    // (raw 已在 useSSEChat console.warn 给开发).
                    return (
                      <div
                        key={`err-${i}`}
                        className="aiClerk-msg__system-error"
                        role="alert"
                      >
                        <span
                          className="aiClerk-msg__system-error-icon"
                          aria-hidden="true"
                        >
                          ⚠
                        </span>
                        <span className="aiClerk-msg__system-error-text">
                          {b.message}
                        </span>
                      </div>
                    )
                  }
                  return null
                })}
                {showProcessing && (
                  <ThinkingPlaceholder
                    label="正在处理"
                    latestThought={
                      showReasoning
                        ? pickLatestThoughtLine(message.parts)
                        : undefined
                    }
                  />
                )}
              </div>
            )}
            {message.attachments && message.attachments.length > 0 && (
              <div className="aiClerk-msg__attachments">
                {message.attachments.map((att, i) => (
                  <AttachmentRenderer
                    key={i}
                    attachment={att}
                    onAction={onAttachmentAction}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
  messageBubblePropsAreEqual
)

// 给 React DevTools Profiler 显示真名 (memo 包过默认是 "Memo(Anonymous)" 难调试)
MessageBubble.displayName = 'MessageBubble'

// ─── 工具：把 quotes + 用户消息组装成发给后端的纯文本 ─────────

export function buildMessageWithQuotes(
  text: string,
  quotes: QuoteItem[]
): string {
  if (quotes.length === 0) return text
  // 每个 quote 用 formatSelectedAsQuote 输出完整文本（range 是多行结构化块，
  // 单 cell / 文本选区是一行），然后对每一行加 `> ` 前缀（连续 > 块算一个 quote）。
  const quoteBlocks = quotes.map((q) => {
    const full = formatSelectedAsQuote(q)
    return full
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n')
  })
  // 不同 quote 之间空一行分隔，让 parseUserMessage 能分组
  return `${quoteBlocks.join('\n\n')}\n\n${text}`
}
